import { useEffect, useState, useCallback, useRef } from 'react';
import api from '../../api/index.js';
import s from './PriceSources.module.scss';
import Header from '../../components/Header/Header.jsx';
import Loader from '../../components/Loader/Loader.jsx';
import PriceSourceItem from './PriceSourceItem.jsx';
import Popup from '../../components/Popup/Popup.jsx';
import Input from '../../components/Input/Input.jsx';

const groupByApi = (list) => {
  const map = {};
  for (const item of list) {
    const key = item.price_api || '—';
    if (!map[key]) map[key] = [];
    map[key].push(item);
  }
  return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
};

const EMPTY_FORM = { brand_id: '', brand_name: '', price_api: '', comment: '' };

const PriceSources = () => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [commentFilter, setCommentFilter] = useState('');
  const [filterInput, setFilterInput] = useState('');

  // create popup
  const [createShowed, setCreateShowed] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [brands, setBrands] = useState([]);
  const [brandsLoading, setBrandsLoading] = useState(false);
  const [brandSearch, setBrandSearch] = useState('');

  const fetchSources = useCallback(async (comment) => {
    setLoading(true);
    const params = {};
    if (comment) params.comment_contains = comment;
    const data = await api.priceSources.getList(params);
    setLoading(false);
    if (!data.error) {
      setSources(Array.isArray(data) ? data : data.items || []);
    }
  }, []);

  useEffect(() => {
    fetchSources(commentFilter);
  }, [commentFilter, fetchSources]);

  const openCreate = async () => {
    setForm(EMPTY_FORM);
    setBrandSearch('');
    setCreateError('');
    setCreateShowed(true);
    if (brands.length === 0) {
      setBrandsLoading(true);
      const data = await api.brands.getList();
      setBrandsLoading(false);
      if (data.brands) setBrands(data.brands);
    }
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    setCommentFilter(filterInput.trim());
  };

  const handleClearFilter = () => {
    setFilterInput('');
    setCommentFilter('');
  };

  const handleUpdated = (updated) => {
    setSources((prev) =>
      prev.map((s) => (s.brand_id === updated.brand_id ? updated : s))
    );
  };

  const handleSelectBrand = (brand) => {
    setForm((prev) => ({
      ...prev,
      brand_id: brand.brand_id || brand._id || brand.id,
      brand_name: brand.brand_name,
      price_api: brand.api_name || prev.price_api,
    }));
    setBrandSearch(brand.brand_name);
  };

  const handleCreate = async () => {
    if (!form.brand_id) { setCreateError('Выберите бренд'); return; }
    if (!form.price_api.trim()) { setCreateError('Укажите API источник'); return; }
    setCreateLoading(true);
    setCreateError('');
    const body = { brand_id: form.brand_id, price_api: form.price_api.trim() };
    if (form.comment.trim()) body.comment = form.comment.trim();
    const data = await api.priceSources.create(body);
    setCreateLoading(false);
    if (data.error) { setCreateError(data.error); return; }
    setCreateShowed(false);
    fetchSources(commentFilter);
  };

  // remap popup
  const [remapShowed, setRemapShowed] = useState(false);
  const [remapLoading, setRemapLoading] = useState(false);
  const [remapMode, setRemapMode] = useState('all'); // 'all' | 'api'
  const [remapApi, setRemapApi] = useState('');
  const [remapResult, setRemapResult] = useState(null); // { status: 'started'|'running'|'done' }
  const [remapError, setRemapError] = useState('');
  const pollRef = useRef(null);

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const startPolling = () => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      const data = await api.priceSources.remapStatus();
      if (data.error) return;
      setRemapResult(data);
      if (data.status === 'done' || data.status === 'idle') stopPolling();
    }, 4000);
  };

  useEffect(() => stopPolling, []);

  const openRemap = async () => {
    setRemapMode('all');
    setRemapApi('');
    setRemapError('');
    setRemapResult(null);
    setRemapShowed(true);
    // проверяем текущий статус — вдруг скрипт уже запущен
    const status = await api.priceSources.remapStatus();
    if (!status.error) {
      setRemapResult(status);
      if (status.status === 'running' || status.status === 'started') startPolling();
    }
  };

  const handleRemap = async () => {
    if (remapMode === 'api' && !remapApi.trim()) {
      setRemapError('Укажите API');
      return;
    }
    setRemapLoading(true);
    setRemapError('');
    setRemapResult(null);
    const body = remapMode === 'api' ? { price_api: remapApi.trim() } : {};
    const data = await api.priceSources.remapApply(body);
    setRemapLoading(false);
    if (data.error) { setRemapError(data.error); return; }
    setRemapResult(data);
    // 409 = already running, 202 = started — в обоих случаях поллим
    if (data.status === 'started' || data.status === 'running') startPolling();
  };

  const apiList = [...new Set(sources.map((s) => s.price_api).filter(Boolean))].sort();

  const filteredBrands = brandSearch.trim()
    ? brands.filter(
        (b) =>
          b.api_name !== 'api_whitegoods' &&
          b.brand_name.toLowerCase().includes(brandSearch.toLowerCase())
      )
    : brands.filter((b) => b.api_name !== 'api_whitegoods');

  const grouped = groupByApi(sources);

  return (
    <>
      <Header
        title="Источник товаров"
        breadCrumbs={[
          { name: 'Главная', link: '/' },
          { name: 'Бренды и API', link: '/brands' },
          { name: 'Источник товаров', link: '/brands/price-sources' },
        ]}
      />
      <div className={s.wrap}>
        <div className={s.toolbar}>
          <form className={s.filter} onSubmit={handleFilterSubmit}>
            <input
              className={s.filterInput}
              value={filterInput}
              onChange={(e) => setFilterInput(e.target.value)}
              placeholder="Фильтр по комментарию..."
            />
            <button type="submit" className="btn light">
              Найти
            </button>
            {commentFilter && (
              <button
                type="button"
                className="btn grey"
                onClick={handleClearFilter}
              >
                Сбросить
              </button>
            )}
          </form>
          <button className="btn dark" onClick={openCreate}>
            + Добавить
          </button>
          <button className="btn grey" onClick={openRemap}>
            Создать мастеров по списку
          </button>
        </div>

        {loading ? (
          <Loader />
        ) : sources.length === 0 ? (
          <p className={s.empty}>Записей не найдено</p>
        ) : (
          <div className={s.groups}>
            {grouped.map(([apiName, items]) => (
              <div key={apiName} className={s.group}>
                <div className={s.groupHeader}>
                  <span className={s.groupTitle}>{apiName}</span>
                  <span className={s.groupCount}>{items.length}</span>
                </div>
                <ul className={s.list}>
                  {items.map((item) => (
                    <PriceSourceItem
                      key={item._id || item.brand_id}
                      item={item}
                      onUpdated={handleUpdated}
                    />
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>

      {remapShowed && (
        <Popup
          close={() => setRemapShowed(false)}
          title="Создать мастеров по списку"
          loading={remapLoading}
        >
          <div className={s.remapForm}>
            <p className={s.remapDesc}>
              Скрипт проходит по всем правилам источников товаров и делает мастерами
              те товары, чей поставщик (API) указан как основной для бренда.
              <code className={s.code}>артикул</code> и <code className={s.code}>bxid</code> автоматически переносятся со старого мастера.
              <br /><br />
              Если мастером уже является товар из нужного API и нет лишних мастеров
              других поставщиков — пара бренд × категория пропускается без изменений.
            </p>
            <div className={s.remapModes}>
              <label className={s.remapModeLabel}>
                <input
                  type="radio"
                  name="remapMode"
                  value="all"
                  checked={remapMode === 'all'}
                  onChange={() => { setRemapMode('all'); setRemapApi(''); setRemapError(''); }}
                />
                <span>По всем источникам</span>
              </label>
              <label className={s.remapModeLabel}>
                <input
                  type="radio"
                  name="remapMode"
                  value="api"
                  checked={remapMode === 'api'}
                  onChange={() => setRemapMode('api')}
                />
                <span>По конкретному API</span>
              </label>
            </div>
            {remapMode === 'api' && (
              <div className={s.remapApiSelect}>
                {apiList.map((api) => (
                  <button
                    key={api}
                    type="button"
                    className={'btn small ' + (remapApi === api ? 'dark' : 'grey')}
                    onClick={() => { setRemapApi(api); setRemapError(''); }}
                  >
                    {api}
                  </button>
                ))}
              </div>
            )}
            {remapError && <p className={s.error}>{remapError}</p>}
            {remapResult && remapResult.status === 'running' && (
              <div className={s.remapStatus + ' ' + s.remapRunning}>
                ⏳ Скрипт уже выполняется{remapResult.started_at ? `, запущен в ${remapResult.started_at}` : ''}. Ожидаем завершения...
              </div>
            )}
            {remapResult && remapResult.status === 'started' && (
              <div className={s.remapStatus + ' ' + s.remapRunning}>
                ⏳ Переназначение запущено в фоне. Ожидаем завершения...
              </div>
            )}
            {remapResult && remapResult.status === 'done' && (
              <div className={s.remapStatus + ' ' + s.remapDone}>
                ✓ Готово. Переназначение завершено.
                {remapResult.message && <p className={s.remapMsg}>{remapResult.message}</p>}
              </div>
            )}
            {(!remapResult || remapResult.status === 'idle') && (
              <button className="btn dark" onClick={handleRemap}>
                Запустить
              </button>
            )}
            {remapResult && remapResult.status === 'done' && (
              <button className="btn grey" onClick={() => setRemapResult(null)}>
                Запустить ещё раз
              </button>
            )}
          </div>
        </Popup>
      )}

      {createShowed && (
        <Popup
          close={() => setCreateShowed(false)}
          title="Добавить источник"
          loading={createLoading}
        >
          <div className={s.form}>
            <div className={s.brandSelect}>
              <label className={s.formLabel}>Бренд</label>
              <input
                className={s.filterInput}
                value={brandSearch}
                onChange={(e) => {
                  setBrandSearch(e.target.value);
                  setForm((prev) => ({ ...prev, brand_id: '', brand_name: '' }));
                }}
                placeholder="Поиск бренда..."
              />
              {brandsLoading && <p className={s.empty}>Загрузка...</p>}
              {!brandsLoading && (
                <ul className={s.brandDropdown} style={{ display: brandSearch && !form.brand_id ? 'block' : 'none' }}>
                  {filteredBrands.slice(0, 50).map((b) => (
                    <li
                      key={b.brand_id || b._id}
                      className={s.brandDropdownItem}
                      onClick={() => handleSelectBrand(b)}
                    >
                      <span>{b.brand_name}</span>
                      {b.api_name && (
                        <span className={s.dropdownApi}>{b.api_name}</span>
                      )}
                    </li>
                  ))}
                  {filteredBrands.length === 0 && (
                    <li className={s.brandDropdownEmpty}>Не найдено</li>
                  )}
                </ul>
              )}
              {form.brand_id && (
                <p className={s.selectedBrand}>✓ {form.brand_name}</p>
              )}
            </div>
            <Input
              label="API источник"
              val={form.price_api}
              setVal={(v) => setForm((prev) => ({ ...prev, price_api: v }))}
              placeholder="например: api_equip"
            />
            <Input
              label="Комментарий"
              val={form.comment}
              setVal={(v) => setForm((prev) => ({ ...prev, comment: v }))}
              placeholder="например: СТМ"
            />
            {createError && <p className={s.error}>{createError}</p>}
            <button className="btn dark" onClick={handleCreate}>
              Создать
            </button>
          </div>
        </Popup>
      )}
    </>
  );
};

export default PriceSources;
